import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../components/elements/Card';
import * as Unicons from '@iconscout/react-unicons';
import { useHistory, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import ToolBox from '../../components/elements/ToolBox';
import DataTables from '../../components/elements/DataTables';
import { renderDate } from '../../utilities/renderDate';
import CreateMasterCategory from './CreateMasterLevel';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import {
  deleteMasterLevel,
  editMasterLevel,
  getMasterLevel,
  postMasterLevel
} from '../../services/masterLevel';
import CreateMasterLevel from './CreateMasterLevel';
import {
  getSharepointCdnFile,
  getSharepointCdnFileMedia
} from '../../utilities';
import { Image, Popover } from '@mantine/core';
import ModalDelete from '../../components/fragments/ModalDelete/ModalDelete';

const columns = [
  {
    heading: 'Level Name',
    props: 'levelName',
    id: 'levelName',
    sortable: true
  },
  {
    heading: 'Level Image',
    props: (item) => renderImage(item),
    id: 'levelImage',
    sortable: true
  },
  {
    heading: 'XP Minimum',
    props: 'xpMinimum',
    id: 'xpMinimum',
    sortable: true
  },
  {
    heading: 'XP Maksimum',
    props: 'xpMaksimum',
    id: 'xpMaksimum',
    sortable: true
  },
  {
    heading: 'Status',
    props: 'status',
    id: 'status',
    sortable: true
  },
  {
    heading: 'Action',
    props: 'action',
    sortable: false
  }
];

const renderDateFormat = (item) => renderDate(item.dateIn, 'datetimeyear');

const renderImage = (item) => {
  return (
    <Popover width={200} position="bottom" withArrow shadow="md">
      <Popover.Target>
        <Image
          src={getSharepointCdnFileMedia(item.levelImage)}
          w={40}
          radius="md"
          style={{ cursor: 'pointer' }}
        />
      </Popover.Target>

      <Popover.Dropdown>
        <Image
          src={getSharepointCdnFileMedia(item.levelImage)}
          width={'100%'}
        />
      </Popover.Dropdown>
    </Popover>
  );
};

const MasterLevelPage = (props) => {
  const history = useHistory();
  const location = useLocation();

  const queryParams: any = new URLSearchParams(location.search);

  const paramsObject = {
    search: queryParams.get('search'),
    page: queryParams.get('page'),
    size: queryParams.get('size')
  };

  const [modalConfig, setModalConfig] = useState({
    show: false,
    data: null,
    type: ''
  });

  const [filter, setFilter] = useState({
    search: '',
    page: 1,
    size: 10
  });

  // Queries
  const fetchedMasterLevel = useQuery({
    queryKey: ['getMasterLevel'],
    queryFn: () =>
      getMasterLevel({
        search: filter.search,
        page: filter.page,
        size: filter.size
      })
  });

  const createMasterLevel = useMutation({
    mutationFn: postMasterLevel,
    onSuccess: () => {
      fetchedMasterLevel.refetch();
      handleCloseModal();
      showNotification({
        type: 'success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const updateMasterLevel = useMutation({
    mutationFn: editMasterLevel,
    onSuccess: () => {
      fetchedMasterLevel.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Update Master Level',
        message: 'Updated data will be show in the table'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const removeMasterLevel = useMutation({
    mutationFn: deleteMasterLevel,
    onSuccess: () => {
      fetchedMasterLevel.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Delete Master Level',
        message: 'Delete Master Level success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  useEffect(() => {
    if (filter?.search || filter?.page || filter?.size) {
      fetchedMasterLevel.refetch();
    }
  }, [filter]);

  useEffect(() => {
    if (paramsObject?.search || paramsObject?.page || paramsObject?.size) {
      setFilter({
        search: paramsObject?.search || '',
        page: paramsObject?.page || 1,
        size: paramsObject?.size || 10
      });
    }
  }, [paramsObject?.search, paramsObject?.page, paramsObject?.size]);

  const buttonOnClick = () => {
    setModalConfig({
      show: true,
      data: null,
      type: 'add'
    });
  };

  const handleSearch = useCallback((data: any) => {
    history.push({
      pathname: '/management/master-level',
      search: `?search=${data}&page=1`
    });
  }, []);

  const handleCloseModal = () => {
    setModalConfig({
      show: false,
      data: null,
      type: ''
    });
  };

  const handleDeleteData = (data) => {
    removeMasterLevel.mutate(data);
  };

  const handleSubmitData = (data) => {
    const payload = {
      ...data,
      userIn:
        modalConfig.type === 'edit'
          ? modalConfig.data.userIn
          : props.auth.binusianId,
      userUp: modalConfig.type === 'edit' ? props.auth.binusianId : ''
    };

    if (modalConfig.type === 'edit') {
      updateMasterLevel.mutate({
        ...payload,
        levelID: modalConfig.data.levelID
      });
      return;
    }
    createMasterLevel.mutate(payload);
    return;
  };
  return (
    <AdminLayout>
      <Breadcrumbs
        pageTitle="Level"
        itemList={[
          {
            title: 'Back Office BCL&D',
            href: '#',
            icon: <Unicons.UilFileCopyAlt size={16} />
          },
          {
            title: 'Reward & Gamification',
            href: '#'
          },
          {
            title: 'Level',
            href: '/management/master-level'
          }
        ]}
      />
      <Card>
        <ToolBox
          handleSearch={handleSearch}
          buttonOnClick={buttonOnClick}
          buttonText="Create Level"
        />
        <DataTables
          columns={columns}
          loading={fetchedMasterLevel?.isLoading}
          data={fetchedMasterLevel?.data?.data?.data || []}
          handleDelete={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'delete'
            });
          }}
          handleEdit={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'edit'
            });
          }}
        />
      </Card>

      <CreateMasterLevel
        isVisible={
          modalConfig.show &&
          (modalConfig.type === 'add' || modalConfig.type === 'edit')
        }
        type={modalConfig.type}
        // data={modalConfig.data}
        data={{
          ...modalConfig.data,
          status:
            modalConfig.type === 'edit'
              ? modalConfig.data.status === 'Y'
                ? 'Active'
                : 'InActive'
              : ''
        }}
        onClose={handleCloseModal}
        onSubmit={handleSubmitData}
        loading={createMasterLevel?.isLoading}
      />

      <ModalDelete
        title="Master Level"
        isVisible={modalConfig.show && modalConfig.type === 'delete'}
        loading={removeMasterLevel.isLoading}
        onClose={handleCloseModal}
        onConfirmation={() =>
          handleDeleteData({
            levelID: modalConfig.data.levelID,
            userUp: props.auth.binusianId
          })
        }
      />
    </AdminLayout>
  );
};

export default MasterLevelPage;
